import { makeAutoObservable } from "mobx"
import React from "react"

import {
    message_CreateMessageRequest,
    message_CreateMessageAttachment,
    message_MessageAttachment,
    message_UpdateMessage,
    MessageAdminService,
    MessagesAdminService,
    PreviewService,
    ChatbotAdminService,
} from "src/api"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { environment, DEFAULT_ACCESS_GROUP } from "src/config"
import { parseDate } from "src/lib/date"
import { persistFiles } from "src/lib/file"
import { FormFields } from "src/lib/form-fields"
import { createLoadingKeys } from "src/lib/loading"
import {
    IAccessGroups,
    IFormFields,
} from "src/modals/notice-board-post-detail/types"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"

type IPersistedNoticeBoardPostFile = Omit<IPersistedFile, "type"> & {
    type: "image" | "doc"
}

const flutterBaseUrl = environment.FLUTTER_BASE_URL

export class NoticeBoardPostDetailStore {
    static Context = React.createContext<NoticeBoardPostDetailStore | null>(
        null,
    )
    static LoadingKeys = createLoadingKeys("init", "submit", "preview")
    fields = new FormFields<IFormFields>({
        admin_title: "",
        title: "",
        text: "",
        access_type: "",
        pinned: false,
        send_push: true,
        publish_at: null,
        unpublish_at: null,
        event_ends_at: null,
        event_starts_at: null,
        include_in_chatbot: false,
        segment_ids: [],
        imageAttachments: [],
        documentAttachments: [],
        accessGroupId: undefined,
        dimensionIds: [],
        agentTypes: [],
        agentInstances: {},
    })

    private id?: number
    private _accessGroups?: IAccessGroups[]

    // Agent-related properties
    agentAccessTypes: IGenericAutoCompleteTextFieldValue[] = []
    agentDefinitions: import("src/api").config_ConfigDefinitionItem[] = []
    agentInstances = new Map<string, IGenericAutoCompleteTextFieldValue[]>()
    isLoadingAgentTypes = false

    constructor() {
        makeAutoObservable(this)
    }

    private isValidAccessGroup = (groupId: number): boolean =>
        this._accessGroups?.some((x) => x.id === groupId) ?? false

    get isEditMode() {
        return this.id != null
    }

    setAccessGroups(accessGroups: IAccessGroups[]) {
        this._accessGroups = accessGroups
    }

    determineAccessGroupId = (
        mode: string | undefined,
        responseAccessGroupId: number,
        accessGroupId: number,
    ) => {
        if (mode === "Copy") {
            this.fields.setError("accessGroupId", "")
            return accessGroupId
        }
        return this.isValidAccessGroup(responseAccessGroupId)
            ? responseAccessGroupId
            : accessGroupId
    }

    @loads(() => NoticeBoardPostDetailStore.LoadingKeys.init)
    async init(accessGroupId: number, id?: number, mode?: string) {
        if (mode !== "Copy") {
            this.setId(id)
        }

        // Load agent types for the agent selection UI
        await this.loadAgentTypes()

        const initialFields =
            id != null
                ? await this.fetchExistingMessage(id, mode, accessGroupId)
                : this.getDefaultFields(accessGroupId)

        this.fields.init(initialFields)
    }

    private async loadAgentTypes(): Promise<void> {
        try {
            this.isLoadingAgentTypes = true

            // Use the same endpoint as information items and knowledge items
            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {
                        space: "knowledgebase",
                    },
                )

            if (
                configDefinition.definition != null &&
                configDefinition.definition.length > 0
            ) {
                // Set agentDefinitions for AgentAccess component
                this.agentDefinitions = configDefinition.definition

                // Convert to agentAccessTypes for the dropdown
                this.agentAccessTypes = configDefinition.definition
                    .map((field) => ({
                        id: field.config_definition_id ?? 0,
                        label: field.name ?? "Unknown Agent",
                    }))
                    .filter((type) => type.id > 0)
            } else {
                this.agentAccessTypes = []
                this.agentDefinitions = []
            }
        } catch (error) {
            this.agentAccessTypes = []
            this.agentDefinitions = []
        } finally {
            this.isLoadingAgentTypes = false
        }
    }

    private async fetchExistingMessage(
        id: number,
        mode?: string,
        accessGroupId?: number,
    ): Promise<IFormFields> {
        const response = await MessageAdminService.noticeBoardGetMessage({
            messageId: id,
        })

        // Load agent connections if this message is included in chatbot
        const agentConnections =
            response.include_in_chatbot === true
                ? this.loadAgentConnectionsFromMessage(response)
                : { agentTypes: [], agentInstances: {} }

        return {
            admin_title: response.admin_title ?? "",
            title: response.title ?? "",
            text: response.text ?? "",
            pinned: response.pinned ?? false,
            access_type:
                mode === "Copy" ? "WRITER" : response.access_type ?? "",
            send_push: response.send_push ?? false,
            publish_at: parseDate(response.publish_at),
            unpublish_at: parseDate(response.unpublish_at),
            event_starts_at: parseDate(response.event_starts_at),
            event_ends_at: parseDate(response.event_ends_at),
            segment_ids: response.segment_ids ?? [],
            dimensionIds: response.dimension_ids ?? [],
            imageAttachments: this.toPersistedFiles(
                response.attachments ?? [],
                "image",
            ),
            documentAttachments: this.toPersistedFiles(
                response.attachments ?? [],
                "doc",
            ),
            include_in_chatbot: response.include_in_chatbot ?? false,
            accessGroupId: this.determineAccessGroupId(
                mode,
                response.access_group_id,
                accessGroupId ?? DEFAULT_ACCESS_GROUP.id,
            ),
            agentTypes: mode === "Copy" ? [] : agentConnections.agentTypes,
            agentInstances:
                mode === "Copy" ? {} : agentConnections.agentInstances,
        }
    }

    private getDefaultFields(accessGroupId: number): IFormFields {
        return {
            admin_title: "",
            title: "",
            text: "",
            pinned: false,
            access_type: "",
            send_push: false,
            publish_at: null,
            unpublish_at: null,
            event_starts_at: null,
            event_ends_at: null,
            segment_ids: [],
            imageAttachments: [],
            documentAttachments: [],
            include_in_chatbot: false,
            accessGroupId,
            agentTypes: [],
            agentInstances: {},
        }
    }

    getDetailsPreviewUrl = async () => {
        const detailsPreviewUrl =
            flutterBaseUrl + "notice-board-details-preview?base64="

        try {
            const url = await PreviewService.postV1PreviewMessage({
                request: {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                    segment_ids: this.fields.data.segment_ids ?? [],
                },
            })

            return `${detailsPreviewUrl}${encodeURIComponent(url)}`
        } catch (error) {
            return ""
        }
    }

    getListPreviewUrl = async () => {
        const listPreviewUrl = flutterBaseUrl + "notice-board-preview?base64="

        try {
            const url = await PreviewService.postV1PreviewMessageList({
                request: {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                    segment_ids: [],
                },
            })

            return `${listPreviewUrl}${encodeURIComponent(url)}`
        } catch (error) {
            return ""
        }
    }

    // Load agent configurations for the current message
    private loadAgentConnectionsFromMessage(
        message: import("src/api").message_Message,
    ) {
        try {
            // Extract agent connections from message response
            const featureConfigIds = message.feature_config_ids ?? []

            // Convert feature config IDs to IGenericAutoCompleteTextFieldValue format
            const agentTypes = featureConfigIds.map((id: number) => {
                // Find the corresponding agent type from loaded options
                const agentType = this.agentAccessTypes.find(
                    (agent) =>
                        (typeof agent.id === "string"
                            ? parseInt(agent.id)
                            : agent.id) === id,
                )
                return (
                    agentType ?? {
                        id: id,
                        label: `Agent ${id}`,
                    }
                )
            })

            return {
                agentTypes,
                agentInstances: {}, // TODO: Implement instance mapping if needed
            }
        } catch (error) {
            return { agentTypes: [], agentInstances: {} }
        }
    }

    // Connect agents to a message
    private async connectAgents(messageId: number) {
        const { data } = this.fields

        if (!data.include_in_chatbot) {
            return
        }

        try {
            // Extract agent IDs from the form data
            const configTypeIds = data.agentTypes
                .map((agent) => {
                    // Use the id directly from the interface
                    return typeof agent.id === "string"
                        ? parseInt(agent.id)
                        : agent.id
                })
                .filter((id) => !isNaN(id))

            const configIds = Object.values(data.agentInstances)
                .flat()
                .map((agent) => {
                    // Use the id directly from the interface
                    return typeof agent.id === "string"
                        ? parseInt(agent.id)
                        : agent.id
                })
                .filter((id) => !isNaN(id))

            // Call the API to connect agents to the message
            await MessagesAdminService.putV1AdminMessageConnectAgents({
                messageId,
                request: {
                    config_type_ids: configTypeIds,
                    config_ids: configIds,
                },
            })
        } catch (error) {
            throw error
        }
    }

    // Agent selection methods
    get formFields() {
        return this.fields
    }

    getAgentTypeValue(): IGenericAutoCompleteTextFieldValue[] {
        return this.fields.get("agentTypes") ?? []
    }

    async setSelectedAgentTypes(
        agentTypes: IGenericAutoCompleteTextFieldValue[],
    ): Promise<void> {
        this.fields.set("agentTypes", agentTypes)

        // Load agent instances for the selected types
        for (const agentType of agentTypes) {
            if (agentType.label != null) {
                await this.fetchAgentInstances(agentType.label)
            }
        }
    }

    setSelectedInstancesForAgentType(
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ): void {
        const currentInstances = this.fields.get("agentInstances") ?? {}
        this.fields.set("agentInstances", {
            ...currentInstances,
            [agentName]: instances,
        })
        this.agentInstances.set(agentName, instances)
    }

    async fetchAgentInstances(agentName: string): Promise<void> {
        try {
            // Use the message connectable agents endpoint with agent_type parameter
            const response =
                await MessagesAdminService.getV1AdminMessageConnectableAgents({
                    agentType: agentName,
                })

            const instances = (response.configs ?? []).map((config) => ({
                id: config.feature_config_id ?? 0,
                label: config.name ?? "Unknown Instance",
            }))

            this.agentInstances.set(agentName, instances)
        } catch (error) {
            this.agentInstances.set(agentName, [])
        }
    }

    // Method to check if an agent type should be deselected
    deselectAgentType?: (agentTypeId: number) => void = (
        agentTypeId: number,
    ) => {
        const currentAgentTypes = this.getAgentTypeValue()
        const filteredAgentTypes = currentAgentTypes.filter(
            (agent) => agent.id !== agentTypeId,
        )
        this.fields.set("agentTypes", filteredAgentTypes)
    }

    // Additional methods required by AgentInstances component
    getSelectedAgentTypesForInstances(): import("src/api").config_ConfigDefinitionItem[] {
        const selectedTypes = this.getAgentTypeValue()
        return this.agentDefinitions.filter((definition) =>
            selectedTypes.some(
                (selected) => selected.id === definition.config_definition_id,
            ),
        )
    }

    getSelectedInstancesForAgentType(
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] {
        const instances = this.fields.get("agentInstances") ?? {}
        return instances[agentName] ?? []
    }

    getInstancesForAgentType(
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] {
        return this.agentInstances.get(agentName) ?? []
    }

    hasExistingInstanceConfigurations(): boolean {
        const instances = this.fields.get("agentInstances") ?? {}
        return Object.keys(instances).length > 0
    }

    @loads(() => NoticeBoardPostDetailStore.LoadingKeys.submit)
    async submit(mode: string) {
        const { data } = this.fields

        await this.fields.catchErrors(async () => {
            if (this.id == null || mode === "Copy") {
                this.validateFields()

                if (this.fields.hasErrors()) return
                const request: message_CreateMessageRequest = {
                    attachments: await this.getAttachments(),
                    segment_ids: data.segment_ids ?? [],
                    ...this.getRequestData(),
                }

                const response =
                    await MessageAdminService.noticeBoardCreateMessage({
                        request,
                    })

                if (response?.message_id != null) {
                    await MessageAdminService.noticeBoardPublishMessage({
                        messageId: response.message_id,
                        request: {
                            published_in: data.segment_ids,
                            dimension_ids: data.dimensionIds,
                        },
                    })

                    // Connect agents to the created message
                    await this.connectAgents(response.message_id)

                    Channel.send({
                        name: "repository/updated",
                        payload: {
                            repository: "messages",
                            action: "create",
                            item: {
                                id: response.message_id ?? 0,
                                name: data.title,
                            },
                        },
                    })
                }
            } else {
                // TODO: remove validation check, when this is added in API
                // Update mode is missing required fields validation on API side. Only create mode has it.
                this.validateFields()
                if (this.fields.hasErrors()) return

                let updateRequest: message_UpdateMessage = {
                    attachments: await this.getAttachments(),
                    ...this.getRequestData(),
                }

                await MessageAdminService.noticeBoardUpdateMessage({
                    request: updateRequest,
                    messageId: this.id,
                })

                await MessageAdminService.noticeBoardPublishMessage({
                    messageId: this.id,
                    request: {
                        published_in: data.segment_ids,
                        dimension_ids: data.dimensionIds,
                    },
                })

                // Connect agents to the updated message
                await this.connectAgents(this.id)

                Channel.send({
                    name: "repository/updated",
                    payload: {
                        repository: "messages",
                        action: "update",
                        item: {
                            id: this.id,
                            name: data.title,
                        },
                    },
                })
            }
        })
    }

    private validateFields(): void {
        this.fields.validateRequiredFields([
            { field: "title" },
            { field: "admin_title" },
            { field: "accessGroupId" },
        ])
    }

    private getRequestData() {
        const { data } = this.fields

        return {
            access_group_id: this.fields.get("accessGroupId") ?? 0,
            admin_title: data.admin_title.trim(),
            pinned: data.pinned,
            publish_at: data.publish_at?.toISOString(),
            send_push: data.send_push,
            text: data.text,
            title: data.title.trim(),
            unpublish_at: data.unpublish_at?.toISOString(),
            include_in_chatbot: data.include_in_chatbot,
            ...(data.event_starts_at !== null && {
                event_starts_at: data.event_starts_at.toISOString(),
            }),
            ...(data.event_ends_at !== null && {
                event_ends_at: data.event_ends_at.toISOString(),
            }),
        }
    }

    private getAttachments = async () => {
        const { data } = this.fields

        return this.toMessageAttachments(
            await this.persistAttachments(
                data.documentAttachments,
                data.imageAttachments,
            ),
        )
    }

    private setId(id?: number) {
        this.id = id
    }

    private toMessageAttachments(
        files: IPersistedNoticeBoardPostFile[],
    ): message_CreateMessageAttachment[] {
        return files.map((file) => ({
            name: file.name,
            url: file.url,
            // The api docs are incorrect so we need to type assert this.
            attachment_type:
                file.type as message_CreateMessageAttachment["attachment_type"],
        }))
    }

    private toPersistedFiles(
        messageAttachments: message_MessageAttachment[],
        type: "image" | "doc",
    ) {
        return messageAttachments
            .filter((attachment) => attachment.attachment_type === type)
            .map((attachment) => this.toPesistedFile(attachment, type))
    }

    private toPesistedFile(
        messageAttachment: message_MessageAttachment,
        type: "image" | "doc",
    ): IPersistedFile {
        return {
            name: messageAttachment.name ?? "",
            url: messageAttachment.url ?? "",
            // The document type is called "doc" for this particular api. Rename
            // it to "document", to be consistent with other api:s.
            type: type === "doc" ? "document" : type,
        }
    }

    private async persistAttachments(
        documents: IFile[],
        images: IFile[],
    ): Promise<IPersistedNoticeBoardPostFile[]> {
        const persistedDocuments = await persistFiles(documents, "document")
        const persistedImages = await persistFiles(images, "image")
        return [...persistedDocuments, ...persistedImages].map((file) => ({
            ...file,
            // The document type is called "doc" for this particular api. Map
            // back type for documents from `document` to `doc`.
            type: file.type === "document" ? ("doc" as const) : file.type,
        }))
    }
}
